import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const W = 900;
const H = 340;
const M = { left: 48, right: 16, top: 12, bottom: 34 };
const WARN = 80;
const CRITICAL = 90;

// A tidy top for the scale: 1, 2, 5 or 10 times a power of ten
export const niceMax = (v) => {
  if (!(v > 0)) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  return [1, 2, 5, 10].map((m) => m * p).find((c) => c >= v);
};
const fmt = (t, withDate) => {
  const d = new Date(t);
  return withDate ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// A chart for one reading (CPU, memory or a drive) with a scale, time labels and a readout that follows the pointer
function ResourceChart({ title, subtitle, values, timestamps, color, onClose, unit = '%', max = 100, warn = WARN, critical = CRITICAL }) {
  const [hover, setHover] = useState(null);
  const GRID = [0, 1, 2, 3, 4, 5].map((k) => Math.round(((max * k) / 5) * 100) / 100);
  const level = (v) => (v == null ? 'default' : critical != null && v >= critical ? 'error' : warn != null && v >= warn ? 'warning' : 'success');
  const real = values.filter((v) => v != null);
  const stat = real.length ? { latest: real[real.length - 1], average: Math.round((real.reduce((a, b) => a + b, 0) / real.length) * 10) / 10, peak: Math.max(...real) } : null;
  const x = (i) => M.left + (i / Math.max(values.length - 1, 1)) * (W - M.left - M.right);
  const y = (v) => M.top + (1 - Math.min(Math.max(v, 0), max) / max) * (H - M.top - M.bottom);
  const withDate = timestamps.length > 1 && timestamps[timestamps.length - 1] - timestamps[0] > 36 * 3600000;

  const segments = [];
  let run = [];
  values.forEach((v, i) => { if (v == null) { if (run.length) segments.push(run); run = []; } else run.push([x(i), y(v)]); });
  if (run.length) segments.push(run);

  const ticks = [0, 1, 2, 3, 4].map((k) => Math.round((k / 4) * (timestamps.length - 1)));

  const move = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - box.left) / box.width) * W;
    const i = Math.round(((px - M.left) / (W - M.left - M.right)) * (values.length - 1));
    setHover(Math.min(Math.max(i, 0), values.length - 1));
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="chart-title">
      <DialogTitle id="chart-title" sx={{ pr: 6 }}>
        {title}
        {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
        <IconButton aria-label="Close chart" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {stat && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Chip size="small" color={level(stat.latest)} label={`Latest ${stat.latest}${unit}`} />
            <Chip size="small" variant="outlined" label={`Average ${stat.average}${unit}`} />
            <Chip size="small" variant="outlined" color={level(stat.peak)} label={`Highest ${stat.peak}${unit}`} />
          </Box>
        )}
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={`${title} over time`} onMouseMove={move} onMouseLeave={() => setHover(null)} style={{ display: 'block', maxHeight: '60vh' }}>
          {GRID.map((g) => (
            <g key={g}>
              <line x1={M.left} x2={W - M.right} y1={y(g)} y2={y(g)} stroke={g === 0 ? '#9e9e9e' : '#e0e0e0'} strokeWidth="1" />
              <text x={M.left - 6} y={y(g) + 4} textAnchor="end" fontSize="12" fill="#616161">{g}{unit}</text>
            </g>
          ))}
          {warn != null && <line x1={M.left} x2={W - M.right} y1={y(warn)} y2={y(warn)} stroke="#ed6c02" strokeDasharray="4 4" />}
          {critical != null && <line x1={M.left} x2={W - M.right} y1={y(critical)} y2={y(critical)} stroke="#d32f2f" strokeDasharray="4 4" />}
          {ticks.map((i, k) => timestamps[i] && (
            <text key={k} x={x(i)} y={H - 10} textAnchor={k === 0 ? 'start' : k === ticks.length - 1 ? 'end' : 'middle'} fontSize="12" fill="#616161">{fmt(timestamps[i], withDate)}</text>
          ))}
          {segments.map((seg, i) => (seg.length === 1
            ? <circle key={i} cx={seg[0][0]} cy={seg[0][1]} r="2.5" fill={color} />
            : <polyline key={i} points={seg.map((p) => p.join(',')).join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />))}
          {hover != null && (
            <g>
              <line x1={x(hover)} x2={x(hover)} y1={M.top} y2={H - M.bottom} stroke="#616161" strokeDasharray="2 3" />
              {values[hover] != null && <circle cx={x(hover)} cy={y(values[hover])} r="4" fill={color} stroke="#fff" strokeWidth="1.5" />}
            </g>
          )}
        </svg>
        <Typography variant="body2" sx={{ minHeight: 24, mt: 1 }} aria-live="polite">
          {hover != null ? `${fmt(timestamps[hover], true)}: ${values[hover] == null ? 'no reading' : `${values[hover]}${unit}`}` : 'Move the pointer over the chart to read a value.'}
        </Typography>
        {(warn != null || critical != null) && <Typography variant="caption" color="text.secondary">Dashed lines mark {warn != null && `${warn}${unit} (orange)`}{warn != null && critical != null && ' and '}{critical != null && `${critical}${unit} (red)`}.</Typography>}
      </DialogContent>
    </Dialog>
  );
}

export default ResourceChart;
